import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { MapElement } from "PosterEditor/utils/canvas-drawing";
import {
  MapLabel,
  StaticLabel,
  Backdrop,
  Gallery,
} from "PosterEditor/model/poster-project";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { SnapLine } from "PosterEditor/utils/rect-snapping";
import { Point, Size, Rect } from "PosterEditor/model/poster-types";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import {
  MIN_CONTENT_SCALE,
  MIN_CONTENT_OFFSET,
  MAX_CONTENT_OFFSET,
  canPanContent,
  calculateContentSlack,
} from "PosterEditor/utils/canvas-drawing/canvas-drawing-gallery";
import {
  findViewportInCells,
  isImageLoaded,
} from "PosterEditor/utils/gallery-utils";
import { useTouchMode } from "BEDROCK/mobile";

/**
 * Calculates a new position for an element based on keyboard arrow movement.
 * Converts from relative coordinates to pixels, applies movement with grid snapping,
 * then converts back to relative coordinates.
 */
const calculateArrowKeyMovement = (
  currentPosition: Point,
  movement: Point,
  mapSize: Size,
  snapToIncrement: (value: number, increment: number) => number,
  increment: number
): Point => {
  const originInPixels: Point = {
    x: currentPosition.x * mapSize.width,
    y: currentPosition.y * mapSize.height,
  };
  const newPositionInPixels: Point = {
    x: snapToIncrement(originInPixels.x + movement.x, increment),
    y: snapToIncrement(originInPixels.y + movement.y, increment),
  };
  return {
    x: newPositionInPixels.x / mapSize.width,
    y: newPositionInPixels.y / mapSize.height,
  };
};

interface ResizableElement {
  position: Point;
  size: Size;
}

interface ResizeResult {
  position: Point;
  size: Size;
}

/**
 * Calculates position and size changes when resizing an element via drag handles.
 * Handles both leading (top-left) and trailing (bottom-right) resize handles.
 */
const calculateResizeFromDrag = (
  initialRect: Rect,
  cursorPosition: Point,
  centerOffset: Point,
  mapSize: Size,
  dragHandlePosition: "leading" | "trailing",
  snapToIncrement: (value: number, increment: number) => number,
  increment: number
): ResizeResult => {
  const xFromCursor = cursorPosition.x - centerOffset.x;
  const yFromCursor = cursorPosition.y - centerOffset.y;

  const actualRect: Rect = {
    x: initialRect.x * mapSize.width,
    y: initialRect.y * mapSize.height,
    width: initialRect.width * mapSize.width,
    height: initialRect.height * mapSize.height,
  };

  const diff: Point = {
    x: actualRect.x - xFromCursor,
    y: actualRect.y - yFromCursor,
  };

  if (dragHandlePosition === "leading") {
    const result: Rect = {
      x: snapToIncrement(actualRect.x - diff.x, increment),
      y: snapToIncrement(actualRect.y - diff.y, increment),
      width: Math.max(
        0.1,
        snapToIncrement(actualRect.width + diff.x, increment)
      ),
      height: snapToIncrement(actualRect.height + diff.y, increment),
    };
    return {
      position: { x: result.x / mapSize.width, y: result.y / mapSize.height },
      size: {
        width: result.width / mapSize.width,
        height: result.height / mapSize.height,
      },
    };
  } else {
    const result: Size = {
      width: snapToIncrement(actualRect.width - diff.x, increment),
      height: snapToIncrement(actualRect.height - diff.y, increment),
    };
    return {
      position: { x: initialRect.x, y: initialRect.y },
      size: {
        width: result.width / mapSize.width,
        height: result.height / mapSize.height,
      },
    };
  }
};

/**
 * Calculates a new position when dragging an element (not resizing).
 */
const calculateDragPosition = (
  cursorPosition: Point,
  centerOffset: Point,
  mapSize: Size,
  snapToIncrement: (value: number, increment: number) => number,
  increment: number
): Point => {
  const x = cursorPosition.x - centerOffset.x;
  const y = cursorPosition.y - centerOffset.y;
  return {
    x: snapToIncrement(x, increment) / mapSize.width,
    y: snapToIncrement(y, increment) / mapSize.height,
  };
};

export interface ElementDraggingProps {}

/**
 * Constrains pan offset based on available slack in each dimension.
 * If there's no slack in a dimension (slackX or slackY <= 0), offset is locked to 0.
 * If there is slack, offset is constrained to [-0.5, 0.5] range.
 *
 * @param offset - The desired pan offset
 * @param slackX - Available horizontal slack (from calculateContentSlack)
 * @param slackY - Available vertical slack (from calculateContentSlack)
 */
export const constrainOffset = (
  offset: Point,
  slackX: number,
  slackY: number
): Point => {
  const epsilon = 0.001;
  return {
    x:
      slackX > epsilon
        ? Math.max(MIN_CONTENT_OFFSET, Math.min(MAX_CONTENT_OFFSET, offset.x))
        : 0,
    y:
      slackY > epsilon
        ? Math.max(MIN_CONTENT_OFFSET, Math.min(MAX_CONTENT_OFFSET, offset.y))
        : 0,
  };
};

export const useElementDragging = () => {
  const isTouchMode = useTouchMode();
  const { editorUIState } = useEditorUI();
  const {
    project,
    setMapLabel,
    getStaticLabel,
    setStaticLabel,
    getBackdrop,
    setBackdrop,
    removeBackdrop,
    isTemplateEditing,
    getGallery,
    setGallery,
    updateViewportContent,
    imageCache,
  } = usePosterProject();
  const { incrementSizeInPX, nearestIncrementValueToValue } = useSizePresets();
  const [pointerLocationState, setPointerLocationState] = useState<Point | undefined>(
    undefined
  );
  // Ref for synchronous access to pointerLocation (needed for touch handlers)
  const pointerLocationRef = useRef<Point | undefined>(undefined);
  // Wrapper that updates both state and ref
  const setPointerLocation = useCallback((location: Point | undefined) => {
    pointerLocationRef.current = location;
    setPointerLocationState(location);
  }, []);
  // Expose state for components that need reactive updates
  const pointerLocation = pointerLocationState;
  const [initialDraggingCenterOffset, setInitialDraggingCenterOffset] =
    useState<Point | undefined>(undefined);
  const [snapLines, setSnapLines] = useState<{
    vertical: SnapLine | undefined;
    horizontal: SnapLine | undefined;
  }>();
  const [isDragging, setIsDragging] = useState(false);
  const [draggingElement, setDraggingElement] = useState<
    MapElement | undefined
  >(undefined);
  //To reference properties of the dragging element before it was dragged.
  const [initialDraggingRect, setInitialDraggingRect] = useState<
    Rect | undefined
  >();
  const [elementUnderCursor, setElementUnderCursorState] = useState<
    MapElement | undefined
  >(undefined);
  // Ref for synchronous access to elementUnderCursor (needed for touch handlers)
  const elementUnderCursorRef = useRef<MapElement | undefined>(undefined);
  // Wrapper that updates both state and ref
  const setElementUnderCursor = useCallback((element: MapElement | undefined) => {
    elementUnderCursorRef.current = element;
    setElementUnderCursorState(element);
  }, []);
  const [containerSize, setContainerSize] = useState<Size | undefined>();
  const [mapRef, setMapRef] = useState<React.MutableRefObject<any>>();

  // Viewport content panning state
  const [panningViewportId, setPanningViewportId] = useState<
    string | undefined
  >(undefined);
  const [panStartPointer, setPanStartPointer] = useState<Point | undefined>(
    undefined
  );
  const [panStartOffset, setPanStartOffset] = useState<Point | undefined>(
    undefined
  );
  const [panViewportRect, setPanViewportRect] = useState<Rect | undefined>(
    undefined
  );
  const [panImageSize, setPanImageSize] = useState<Size | undefined>(undefined);

  const increment = useMemo(
    () => incrementSizeInPX(project) ?? 10,
    [project.sizeOption]
  );

  // LHS-257: Prevent dragging of static labels, backdrops, and map labels on mobile
  const canDragStaticLabels = !isTouchMode;
  const canDragMapLabels =
    !isTouchMode &&
    (isTemplateEditing || project.editingPermissions.mapLabel.position === true);
  const canDragBackdrops = !isTouchMode;
  const canDragGallery = isTemplateEditing; // Only admins can drag gallery

  // NOTE: Eligibility checks for dragging are computed inside handleMouseDown
  // using the ref for synchronous access (needed for touch handlers on iPad)

  const handleMouseDown = (forceEligible: boolean = false) => {
    // Use refs for synchronous access (needed for touch handlers where state may be stale)
    const currentElement = elementUnderCursorRef.current;
    const currentPointerLocation = pointerLocationRef.current;

    console.log('[DRAG DEBUG] handleMouseDown called, forceEligible:', forceEligible);
    console.log('[DRAG DEBUG] currentElement:', currentElement?.type, 'id:', (currentElement?.element as any)?.id);
    console.log('[DRAG DEBUG] currentPointerLocation:', currentPointerLocation);

    // Recompute eligibility using current ref value (not stale closure)
    // For touch, forceEligible bypasses the editing check since state updates are async
    const currentEditingElementIsElementUnderCursor =
      editorUIState.editingElement?.id === currentElement?.element?.id;
    const currentPointID =
      editorUIState.editingElement?.styledPoint?.label?.pointID ?? "";
    const currentEditingElementIsLabelForPoint =
      editorUIState.editingElement?.styledPoint?.id === currentPointID;

    // Prevent dragging when clicking on a gallery element that's not the editing element
    // This fixes the bug where dragging a viewport would move a selected label
    const isClickingOnUnrelatedGallery =
      currentElement?.type === "gallery" && !currentEditingElementIsElementUnderCursor;

    const currentElementIsEligbleForDragging =
      !isClickingOnUnrelatedGallery &&
      (forceEligible || currentEditingElementIsElementUnderCursor || currentEditingElementIsLabelForPoint);

    console.log('[DRAG DEBUG] eligible:', currentElementIsEligbleForDragging);

    // Check if we're clicking on a selected viewport that can be panned
    const selectedViewportId = editorUIState.selectedViewportId;
    if (
      selectedViewportId &&
      currentElement?.type === "gallery" &&
      currentElement?.viewportId === selectedViewportId &&
      !currentElement?.zoomAction && // Not clicking a zoom button
      currentPointerLocation &&
      project.gallery
    ) {
      const viewport = findViewportInCells(
        project.gallery.content,
        selectedViewportId
      );
      if (viewport && viewport.imageID) {
        // Check if panning is possible based on image/viewport aspect ratio and scale
        const imageElement = imageCache.get(`gallery-${viewport.imageID}`);
        const viewportRect = currentElement.rect;
        const scale = viewport.contentScale ?? MIN_CONTENT_SCALE;

        // Validate image is fully loaded before using dimensions
        if (!isImageLoaded(imageElement)) {
          return;
        }

        console.log(
          "handleMouseDown viewportRect:",
          viewportRect,
          "from currentElement.rect"
        );

        // Check if panning is possible
        if (
          canPanContent(
            imageElement.naturalWidth,
            imageElement.naturalHeight,
            viewportRect.width,
            viewportRect.height,
            scale
          )
        ) {
          // Start panning - viewport is selected, has an image, and has slack to pan
          setPanningViewportId(selectedViewportId);
          setPanStartPointer(currentPointerLocation);
          setPanStartOffset(viewport.contentOffset ?? { x: 0, y: 0 });
          setPanViewportRect(viewportRect);
          setPanImageSize({
            width: imageElement.naturalWidth,
            height: imageElement.naturalHeight,
          });
          return; // Don't proceed with normal dragging
        }
      }
    }

    if (currentElementIsEligbleForDragging) {
      const backdrop = currentElement?.element as Backdrop;
      if (backdrop?.size) {
        setInitialDraggingRect({
          x: backdrop.position.x,
          y: backdrop.position.y,
          width: backdrop.size.width,
          height: backdrop.size.height,
        });
      }
      if (currentElement?.type === "backdrop") {
        if (canDragBackdrops) {
          setDraggingElement(currentElement);
        }
      } else if (currentElement?.type === "staticLabel") {
        if (canDragStaticLabels) {
          setDraggingElement(currentElement);
        }
      } else if (currentElement?.type === "gallery") {
        if (canDragGallery) {
          const gallery = currentElement?.element as Gallery;
          if (gallery?.size) {
            setInitialDraggingRect({
              x: gallery.position.x,
              y: gallery.position.y,
              width: gallery.size.width,
              height: gallery.size.height,
            });
          }
          setDraggingElement(currentElement);
        }
      } else {
        setDraggingElement(currentElement);
      }
    } else {
      setDraggingElement(undefined);
      setIsDragging(false);
      return; // Exit early - element is not eligible for dragging
    }

    // Only reach here if element is eligible for dragging
    if (currentElement && currentPointerLocation) {
      if (currentElement.type === "mapLabel" && !canDragMapLabels) {
        return;
      } else if (
        currentElement.type === "staticLabel" &&
        !canDragStaticLabels
      ) {
        return;
      } else if (currentElement.type === "backdrop" && !canDragBackdrops) {
        return;
      } else if (currentElement.type === "gallery" && !canDragGallery) {
        return;
      }

      let elementPosition: Point = {
        x: currentElement.rect.x,
        y: currentElement.rect.y,
      };
      if (currentElement.type === "staticLabel") {
        const label = currentElement.element as StaticLabel;
        if (label.style.alignment === "center") {
          elementPosition = {
            x: currentElement.rect.x + currentElement.rect.width / 2,
            y: currentElement.rect.y,
          };
        } else if (label.style.alignment === "right") {
          elementPosition = {
            x: currentElement.rect.x + currentElement.rect.width,
            y: currentElement.rect.y,
          };
        }
      }
      const centerOffset = {
        x: currentPointerLocation.x - elementPosition.x,
        y: currentPointerLocation.y - elementPosition.y,
      };

      setInitialDraggingCenterOffset(centerOffset);
      setIsDragging(true);
    } else {
      setDraggingElement(undefined);
      setIsDragging(false);
    }
  };

  const handleMouseUp = () => {
    // Clear panning state
    setPanningViewportId(undefined);
    setPanStartPointer(undefined);
    setPanStartOffset(undefined);
    setPanViewportRect(undefined);
    setPanImageSize(undefined);

    setDraggingElement(undefined);
    setIsDragging(false);
  };

  const handleMouseMove = () => {
    // Handle viewport content panning
    if (
      panningViewportId &&
      panStartPointer &&
      panStartOffset &&
      panViewportRect &&
      panImageSize &&
      pointerLocation &&
      project.gallery
    ) {
      const viewport = findViewportInCells(
        project.gallery.content,
        panningViewportId
      );
      if (viewport) {
        // Calculate pointer delta in screen pixels
        const deltaX = pointerLocation.x - panStartPointer.x;
        const deltaY = pointerLocation.y - panStartPointer.y;

        const currentScale = viewport.contentScale ?? MIN_CONTENT_SCALE;
        const currentSlack = calculateContentSlack(
          panImageSize.width,
          panImageSize.height,
          panViewportRect.width,
          panViewportRect.height,
          currentScale
        );

        // For 1:1 mouse tracking:
        // The offset range [-0.5, 0.5] maps to the full pannable distance in screen pixels.
        // We need to find how many screen pixels correspond to the full offset range (1.0).
        //
        // In the drawing code:
        //   srcX = slackX * (0.5 + offset.x)  // position in image pixels
        //   The image is drawn scaled to fill the viewport
        //
        // The scale factor from image pixels to screen pixels is:
        //   screenScale = viewportWidth / srcWidth = viewportHeight / srcHeight
        //
        // When offset goes from -0.5 to 0.5 (range of 1.0), srcX moves by slackX image pixels.
        // In screen pixels, that's: slackX * screenScale
        //
        // srcWidth = baseSrcWidth / contentScale, so:
        //   screenScale = viewportWidth * contentScale / baseSrcWidth
        //
        // Total pannable screen distance = slackX * viewportWidth * contentScale / baseSrcWidth

        const viewportAspect = panViewportRect.width / panViewportRect.height;
        const imageAspect = panImageSize.width / panImageSize.height;

        // Calculate base source dimensions (what portion of image fills viewport at scale 1.0)
        let baseSrcWidth: number;
        let baseSrcHeight: number;
        if (viewportAspect > imageAspect) {
          baseSrcWidth = panImageSize.width;
          baseSrcHeight = panImageSize.width / viewportAspect;
        } else {
          baseSrcWidth = panImageSize.height * viewportAspect;
          baseSrcHeight = panImageSize.height;
        }

        // Calculate pannable distance in screen pixels for the full offset range [−0.5, 0.5]
        // This is the key formula: slackX (in image pixels) * screen scale factor
        const pannableScreenX =
          (currentSlack.slackX * panViewportRect.width * currentScale) /
          baseSrcWidth;
        const pannableScreenY =
          (currentSlack.slackY * panViewportRect.height * currentScale) /
          baseSrcHeight;

        console.log('[Desktop Pan Debug] Raw delta:', { deltaX, deltaY });
        console.log('[Desktop Pan Debug] Calculation inputs:', {
          slackX: currentSlack.slackX,
          slackY: currentSlack.slackY,
          viewportWidth: panViewportRect.width,
          viewportHeight: panViewportRect.height,
          currentScale,
          baseSrcWidth,
          baseSrcHeight,
          imageWidth: panImageSize.width,
          imageHeight: panImageSize.height,
        });
        console.log('[Desktop Pan Debug] pannableScreen:', { pannableScreenX, pannableScreenY });

        // Convert screen delta to offset delta
        // Offset range is 1.0, so: offsetDelta = screenDelta / pannableScreen
        // Negative because dragging right should show more of right side (decrease offset)
        const offsetDeltaX =
          pannableScreenX > 0.001 ? -deltaX / pannableScreenX : 0;
        const offsetDeltaY =
          pannableScreenY > 0.001 ? -deltaY / pannableScreenY : 0;

        console.log('[Desktop Pan Debug] offsetDelta:', { offsetDeltaX, offsetDeltaY });

        // Calculate new offset
        const newOffset: Point = {
          x: panStartOffset.x + offsetDeltaX,
          y: panStartOffset.y + offsetDeltaY,
        };

        // Constrain offset to valid bounds
        const constrainedOffset = constrainOffset(
          newOffset,
          currentSlack.slackX,
          currentSlack.slackY
        );

        // Update viewport content
        updateViewportContent(
          panningViewportId,
          constrainedOffset,
          currentScale
        );
      }
      return;
    }

    if (!isDragging) {
      return;
    }
    const config: MoveConfig = {
      draggingElement,
      initialRect: initialDraggingRect,
      elementUnderCursor,
      pointerLocation,
      initialDraggingCenterOffset,
      map: mapRef,
      snapLines: snapLines,
      increment: increment,
      onSetMapLabel: (label: MapLabel) => {
        setMapLabel(label);
      },
      onSetStaticLabel: setStaticLabel,
      onSetBackdrop: setBackdrop,
      onSetGallery: setGallery,
      nearestIncrementValueToValue,
    };
    moveWithConfig(config);
  };

  // Listen for these keypresses and move the element.
  const onRightArrowKeyboardButton = () => moveEditingElement(10, 0);
  const onLeftArrowKeyboardButton = () => moveEditingElement(-10, 0);
  const onUpArrowKeyboardButton = () => moveEditingElement(0, -10);
  const onDownArrowKeyboardButton = () => moveEditingElement(0, 10);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // switch (event.key) {
      //   case "ArrowRight":
      //     onRightArrowKeyboardButton();
      //     break;
      //   case "ArrowLeft":
      //     onLeftArrowKeyboardButton();
      //     break;
      //   case "ArrowUp":
      //     onUpArrowKeyboardButton();
      //     break;
      //   case "ArrowDown":
      //     onDownArrowKeyboardButton();
      //     break;
      //   //This isn't reliable.
      //   // case "Delete":
      //   //   deleteEditingElement();
      //   //   break;
      //   // case "Backspace":
      //   //   deleteEditingElement();
      //   //   break;
      //   default:
      //     break;
      //}
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  //Workaround for moving editing element.
  //Without this the moveEditingElement function captures only the first editingElement.
  const editingElementRef = useRef(editorUIState.editingElement);

  useEffect(() => {
    editingElementRef.current = editorUIState.editingElement;
  }, [editorUIState.editingElement]);

  const deleteEditingElement = () => {
    if (editingElementRef.current?.backdrop) {
      const id = editingElementRef.current.backdrop.id;
      if (!id) {
        return;
      }
      const backdrop = getBackdrop(id);
      if (!backdrop) {
        return;
      }
      removeBackdrop(backdrop);
    }
  };

  const moveEditingElement = (x: number, y: number) => {
    const staticLabel = editingElementRef.current?.staticLabel;
    if (staticLabel?.id) {
      const id = editingElementRef.current?.staticLabel?.id;

      if (!id) {
        return;
      }
      const label = getStaticLabel(id);
      if (!label) {
        return;
      }
      const updatedLabel = {
        ...label,
        position: {
          x: nearestIncrementValueToValue(label.position.x + x, increment),
          y: nearestIncrementValueToValue(label.position.y + y, increment),
        },
      };
      setStaticLabel(updatedLabel);
    } else if (editingElementRef.current?.backdrop) {
      const id = editingElementRef.current?.backdrop?.id;
      if (!id) return;

      const backdrop = getBackdrop(id);
      const mapSize = editorUIState.mapSizeInPX;
      if (!backdrop || !mapSize) return;

      const newPosition = calculateArrowKeyMovement(
        backdrop.position,
        { x, y },
        mapSize,
        nearestIncrementValueToValue,
        increment
      );
      setBackdrop({ ...backdrop, position: newPosition });
    } else if (editingElementRef.current?.gallery) {
      const gallery = getGallery();
      const mapSize = editorUIState.mapSizeInPX;
      if (!gallery || !mapSize) return;

      const newPosition = calculateArrowKeyMovement(
        gallery.position,
        { x, y },
        mapSize,
        nearestIncrementValueToValue,
        increment
      );
      setGallery({ ...gallery, position: newPosition });
    }
  };

  // Compute whether we're currently panning a viewport
  const isPanningViewport = panningViewportId !== undefined;

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    setPointerLocation,
    pointerLocation,
    isDragging,
    isPanningViewport,
    setDraggingElement,
    elementUnderCursor,
    setElementUnderCursor,
    setSnapLines,
    setContainerSize,
    setMapRef,
  };
};

interface MoveConfig {
  draggingElement: MapElement | undefined;
  initialRect: Rect | undefined;
  elementUnderCursor: MapElement | undefined;
  pointerLocation: { x: number; y: number } | undefined;
  initialDraggingCenterOffset: { x: number; y: number } | undefined;
  map: React.MutableRefObject<any> | undefined;
  // containerSize: Size;
  snapLines:
    | {
        vertical: SnapLine | undefined;
        horizontal: SnapLine | undefined;
      }
    | undefined;
  increment: number;
  onSetMapLabel: (label: MapLabel) => void;
  onSetStaticLabel: (label: StaticLabel) => void;
  onSetBackdrop: (backdrop: Backdrop) => void;
  onSetGallery: (gallery: Gallery) => void;
  nearestIncrementValueToValue: (value: number, increment: number) => number;
}

const moveWithConfig = (config: MoveConfig) => {
  if (
    config.map &&
    config.draggingElement &&
    (config.draggingElement.type === "mapLabel" ||
      config.draggingElement.type === "staticLabel" ||
      config.draggingElement.type === "backdrop" ||
      config.draggingElement.type === "gallery") &&
    config.pointerLocation
  ) {
    const position = config.pointerLocation;
    const centerOffset = {
      x: config.initialDraggingCenterOffset?.x ?? 0,
      y: config.initialDraggingCenterOffset?.y ?? 0,
    };

    const mapWidth = config.map.current.getContainer().clientWidth;
    const mapHeight = config.map.current.getContainer().clientHeight;

    if (config.draggingElement.type === "mapLabel") {
      let label = config.draggingElement.element as MapLabel;
      const labelCoordinatePoint = config.map.current.project([
        label.coordinate.lng,
        label.coordinate.lat,
      ]);
      const newLabelOffsetFromCoordinate = {
        x:
          position.x -
          centerOffset.x -
          labelCoordinatePoint.x +
          (label.style.padding ?? 0),
        y:
          position.y -
          centerOffset.y -
          labelCoordinatePoint.y +
          (label.style.padding ?? 0),
      };
      let style = label.style;
      style.offset = newLabelOffsetFromCoordinate;
      label.style = style;
      config.onSetMapLabel(label);
    } else if (config.draggingElement.type === "staticLabel") {
      let label = config.draggingElement.element as StaticLabel;

      let xFromCursor = position.x - centerOffset.x;
      let yFromCursor = position.y - centerOffset.y;

      let x = xFromCursor;
      let y = yFromCursor;

      const rect = config.elementUnderCursor?.rect;
      if (rect) {
        //Depending on the label alignment we want to snap to the grid
        //using either the origin, center, or end of the label in the X axis.

        let anchorOffsetX = 0;
        let anchorOffsetY = label.style.padding * mapHeight;
        //We need to get the 'anchor' or the point of the label we want to snap to the grid.
        //Then snap than to the nearest increment, and then calculate the new position of the label.
        if (label.style.alignment === "left") {
          anchorOffsetX = label.style.padding * mapWidth;
          anchorOffsetY = label.style.padding * mapHeight;
        } else if (label.style.alignment === "right") {
          anchorOffsetX = -label.style.padding * mapWidth;
        }
        const newAnchorX = x + anchorOffsetX;
        const newAnchorY = y + anchorOffsetY;

        const newCenterX = newAnchorX;
        const newCenterY = newAnchorY;
        x = newCenterX;
        y = newCenterY;
      }

      const relativeOrigin = {
        x: config.nearestIncrementValueToValue(x, config.increment) / mapWidth,
        y: config.nearestIncrementValueToValue(y, config.increment) / mapHeight,
      };

      label.position = relativeOrigin;
      config.onSetStaticLabel(label);
    } else if (config.draggingElement.type === "backdrop") {
      let backdrop = config.draggingElement.element as Backdrop;
      const dragHandle = config.draggingElement.dragHandleUnderCursor;
      const mapSize = { width: mapWidth, height: mapHeight };

      if (!dragHandle) {
        const newPosition = calculateDragPosition(
          position,
          centerOffset,
          mapSize,
          config.nearestIncrementValueToValue,
          config.increment
        );
        backdrop.position = newPosition;
        config.onSetBackdrop(backdrop);
      } else if (config.initialRect) {
        const resizeResult = calculateResizeFromDrag(
          config.initialRect,
          position,
          centerOffset,
          mapSize,
          dragHandle.position,
          config.nearestIncrementValueToValue,
          config.increment
        );
        backdrop.position = resizeResult.position;
        backdrop.size = resizeResult.size;
        config.onSetBackdrop(backdrop);
        return;
      }
    } else if (config.draggingElement.type === "gallery") {
      let gallery = config.draggingElement.element as Gallery;
      const dragHandle = config.draggingElement.dragHandleUnderCursor;
      const mapSize = { width: mapWidth, height: mapHeight };

      if (!dragHandle) {
        const newPosition = calculateDragPosition(
          position,
          centerOffset,
          mapSize,
          config.nearestIncrementValueToValue,
          config.increment
        );
        gallery = { ...gallery, position: newPosition };
        config.onSetGallery(gallery);
      } else if (config.initialRect) {
        const resizeResult = calculateResizeFromDrag(
          config.initialRect,
          position,
          centerOffset,
          mapSize,
          dragHandle.position,
          config.nearestIncrementValueToValue,
          config.increment
        );
        gallery = {
          ...gallery,
          position: resizeResult.position,
          size: resizeResult.size,
        };
        config.onSetGallery(gallery);
      }
    }
  }
};
